import { useState, useRef } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkEmoji from 'remark-emoji'
import type { ReactNode } from 'react'
import { headingId } from './ContentToc'
import { useAuth } from '../../contexts'
import { useSiteContent, uploadContentImage } from '../../hooks/useSiteContent'

/** Give every heading a stable id, so a table of contents can link to it. */
function headingWithId(level: 2 | 3) {
  const Tag = `h${level}` as const
  return function Heading({ children }: { children?: ReactNode }) {
    const text = typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.map(c => (typeof c === 'string' ? c : '')).join('')
        : ''
    return <Tag id={headingId(text)} className="scroll-mt-20">{children}</Tag>
  }
}

const MARKDOWN_COMPONENTS = {
  h2: headingWithId(2),
  h3: headingWithId(3),
}

/**
 * Plain react-markdown is CommonMark only, which has no tables - a pasted
 * table rendered as a row of pipes. GFM adds tables, strikethrough and
 * autolinks.
 *
 * Emoji turns `:scroll:` into the character, so text pasted out of Discord
 * keeps its icons. Discord's *custom* emoji (`:Pvpraven:`) are images on
 * their server and cannot be resolved here - upload those with the image
 * button instead.
 */
const MARKDOWN_PLUGINS = [remarkGfm, remarkEmoji]

/**
 * A block of text officers can edit in place.
 *
 * Members see rendered markdown. Officers see the same, plus an Edit button
 * that swaps in a textarea with a live preview beside it. Nothing about the
 * page changes for anyone else while an officer is editing.
 *
 * Markdown rather than a rich text editor on purpose: the content is
 * headings, lists and the odd table, it survives being pasted out of Discord,
 * and it stays readable in the database.
 */
/** What each toolbar button does to the text under the cursor. */
type Action =
  | { kind: 'wrap'; before: string; after: string }
  | { kind: 'line'; prefix: string }
  | { kind: 'insert'; text: string }

const TOOLBAR: { label: string; title: string; action: Action }[] = [
  { label: 'H2', title: 'Section heading', action: { kind: 'line', prefix: '## ' } },
  { label: 'H3', title: 'Sub-heading', action: { kind: 'line', prefix: '### ' } },
  { label: 'B', title: 'Bold', action: { kind: 'wrap', before: '**', after: '**' } },
  { label: 'I', title: 'Italic', action: { kind: 'wrap', before: '*', after: '*' } },
  { label: 'List', title: 'Bullet list', action: { kind: 'line', prefix: '- ' } },
  { label: 'Quote', title: 'Quote', action: { kind: 'line', prefix: '> ' } },
  { label: 'Link', title: 'Link', action: { kind: 'wrap', before: '[', after: '](https://)' } },
  {
    label: 'Table',
    title: 'Insert a table',
    action: {
      kind: 'insert',
      text: ['', '| Column | Column |', '|---|---|', '| Value | Value |', '', ''].join('\n'),
    },
  },
  {
    label: 'Rule',
    title: 'Divider',
    action: { kind: 'insert', text: ['', '---', '', ''].join('\n') },
  },
]

export function EditableContent({
  contentKey,
  className = '',
  emptyHint,
}: {
  contentKey: string
  className?: string
  /** Shown to officers when there is nothing here yet; members see nothing. */
  emptyHint?: string
}) {
  const { isOfficer } = useAuth()
  const { content, loading, save, reset } = useSiteContent(contentKey)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  if (loading) return null

  const body = content?.body_md ?? ''

  function startEditing() {
    setDraft(body)
    setError(null)
    setEditing(true)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    const result = await save(draft)
    setSaving(false)
    if (result.error) { setError(result.error); return }
    setEditing(false)
  }

  async function handleReset() {
    setSaving(true)
    const result = await reset()
    setSaving(false)
    if (result.error) { setError(result.error); return }
    setEditing(false)
  }

  /** Apply a toolbar action to the selection, and keep focus in the box. */
  function applyAction(action: Action) {
    const area = textareaRef.current
    if (!area) return
    const start = area.selectionStart
    const end = area.selectionEnd
    const selected = draft.slice(start, end)

    let replacement: string
    if (action.kind === 'wrap') {
      replacement = `${action.before}${selected || 'text'}${action.after}`
    } else if (action.kind === 'line') {
      // Prefix every selected line, so a list can be made from several at once
      const lines = (selected || 'text').split('\n')
      replacement = lines.map(line => `${action.prefix}${line}`).join('\n')
    } else {
      replacement = action.text
    }

    setDraft(draft.slice(0, start) + replacement + draft.slice(end))
    requestAnimationFrame(() => {
      area.focus()
      area.selectionStart = start
      area.selectionEnd = start + replacement.length
    })
  }

  /** Drop an image at the cursor, so it lands where the officer was typing. */
  async function handleImage(file: File) {
    setUploading(true)
    setError(null)
    const { url, error: uploadError } = await uploadContentImage(file)
    setUploading(false)
    if (uploadError || !url) {
      setError(uploadError ?? 'Upload failed')
      return
    }
    const snippet = `\n\n![](${url})\n\n`
    const area = textareaRef.current
    if (area) {
      const at = area.selectionStart
      setDraft(draft.slice(0, at) + snippet + draft.slice(at))
    } else {
      setDraft(draft + snippet)
    }
  }

  if (!editing) {
    if (!body.trim()) {
      if (!isOfficer) return null
      return (
        <div className={className}>
          <button
            onClick={startEditing}
            className="text-sm text-gray-500 hover:text-gray-300 border border-dashed border-gray-700 hover:border-gray-500 rounded-lg px-4 py-3 w-full text-left transition-colors"
          >
            {emptyHint ?? 'Add text here'}
          </button>
        </div>
      )
    }
    return (
      <div className={className}>
        {isOfficer && (
          <div className="flex justify-end mb-2">
            <button
              onClick={startEditing}
              className="text-xs text-gray-500 hover:text-gray-200 px-2 py-1 rounded bg-gray-800/60 hover:bg-gray-800 transition-colors"
            >
              Edit
            </button>
          </div>
        )}
        <div className="prose prose-invert max-w-none prose-headings:text-gray-100 prose-a:text-blue-400 prose-strong:text-gray-100 prose-table:text-sm">
          <Markdown remarkPlugins={MARKDOWN_PLUGINS} components={MARKDOWN_COMPONENTS}>{body}</Markdown>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs text-gray-300 px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            {uploading ? 'Uploading…' : 'Insert image'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) void handleImage(file)
              e.target.value = ''
            }}
          />
          <span className="text-xs text-gray-600">
            Paste straight from Discord — tables and :emoji: work
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!content?.isDefault && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 transition-colors"
              title="Discard all edits and go back to the text that ships with the site"
            >
              Reset to default
            </button>
          )}
          <button
            onClick={() => setEditing(false)}
            className="text-xs text-gray-400 hover:text-gray-200 px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-xs text-white px-3 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      <div className="flex flex-wrap items-center gap-1 mb-2">
        {TOOLBAR.map(button => (
          <button
            key={button.label}
            type="button"
            title={button.title}
            onClick={() => applyAction(button.action)}
            className="text-xs text-gray-300 px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-700 transition-colors min-w-[2rem]"
          >
            {button.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          spellCheck
          className="w-full min-h-[24rem] bg-gray-900 border border-gray-700 rounded-lg p-3 text-sm text-gray-100 font-mono leading-relaxed focus:outline-none focus:border-amber-500"
        />
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3 overflow-auto max-h-[36rem]">
          <div className="prose prose-invert max-w-none prose-headings:text-gray-100 prose-a:text-blue-400 prose-strong:text-gray-100 prose-table:text-sm">
            <Markdown remarkPlugins={MARKDOWN_PLUGINS}>{draft}</Markdown>
          </div>
        </div>
      </div>
    </div>
  )
}
